import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Spinner, Button } from 'react-bootstrap';
import { hasActiveSubscription } from '../utils/subscription';
import { setStoredThemePreference, applyThemeBasedOnAuth, getStoredTheme } from '../utils/theme';
import { fetchLemonSqueezyAPI } from '../utils/lemonsqueezy_api';
import styles from './CheckoutSuccess.module.css';

const CheckoutSuccess: React.FC = () => {
    const [isChecking, setIsChecking] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const activateSubscriptionFlow = async () => {
            try {
                const session_id = searchParams.get('session_id');
                if (!session_id) {
                    setError('Invalid checkout session. Please try again.');
                    setIsChecking(false);
                    return;
                }

                // Get variant_name from query parameters, default to "pro"
                const variant_name = searchParams.get('variant_name') || 'pro';

                // Call the checkout success API
                await fetchLemonSqueezyAPI('/checkout_success', {
                    method: 'POST',
                    body: JSON.stringify({ 
                        session_id,
                        variant_name 
                    })
                });

                // Check if subscription is now active
                const isActive = await hasActiveSubscription();
                setIsActive(isActive);
                
                // Get current theme
                const theme = getStoredTheme();
                setCurrentTheme(theme);
                
                setIsChecking(false);
            } catch (err) {
                console.error('Error activating subscription:', err);
                setError('Failed to activate subscription. Please refresh the page.');
                setIsChecking(false);
            }
        };

        activateSubscriptionFlow();
    }, [searchParams]);

    const handleEnableDarkTheme = async () => {
        try {
            setStoredThemePreference('dark');
            applyThemeBasedOnAuth(true, true);
            navigate('/');
        } catch (err) {
            console.error('Error enabling dark theme:', err);
            setError('Failed to enable dark theme. Please try again later.');
        }
    };

    const variant_name = searchParams.get('variant_name') || 'pro';
    const isTeamSubscription = variant_name === 'team';

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <h2>Thank You!</h2>

                {isChecking && (
                    <div className={styles.checkingStatus}>
                        <Spinner animation="border" role="status" />
                        <p>Please wait...</p>
                    </div>
                )}

                {error && (
                    <div className={styles.error}>
                        <p>{error}</p>
                    </div>
                )}

                {isActive && !isChecking && (
                    <div className={styles.success}>
                        <i className={`bi bi-check-circle-fill ${styles.successIcon}`}></i>
                        <h4 className={styles.successTitle}>
                            Your {isTeamSubscription ? 'TEAM' : 'PRO'} Subscription is Active!
                        </h4>
                        
                        {isTeamSubscription && (
                            <p className={styles.teamMessage}>
                                Add users to your team in the <Link to="/account">Account</Link> page.
                            </p>
                        )}
                        
                        {currentTheme === 'light' && !isTeamSubscription && (
                            <Button 
                                variant="warning" 
                                onClick={handleEnableDarkTheme}
                                className={styles.darkThemeButton}
                            >
                                Enable Dark Theme
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckoutSuccess; 